import { useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router";
import Label from "../form/Label";
import Input from "../form/input/InputField";
import Button from "../ui/button/Button";
import { apiPost } from "../../lib/api";

function useQuery() {
  const location = useLocation();
  return useMemo(() => new URLSearchParams(location.search), [location.search]);
}

export default function ResetPasswordForm() {
  const query = useQuery();
  const navigate = useNavigate();

  const token = query.get("token") || "";
  const emailFromQuery = query.get("email") || "";

  const [email, setEmail] = useState(emailFromQuery);
  const [password, setPassword] = useState("");
  const [passwordConfirmation, setPasswordConfirmation] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showPasswordConfirmation, setShowPasswordConfirmation] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setMessage(null);
    setError(null);

    if (!token) {
      setError("Reset token is missing or invalid.");
      return;
    }

    const trimmedEmail = email.trim();
    if (!trimmedEmail || !password || !passwordConfirmation) {
      setError("Please complete all required fields.");
      return;
    }

    if (password !== passwordConfirmation) {
      setError("Passwords do not match.");
      return;
    }

    setSubmitting(true);
    try {
      const response = await apiPost<{ message: string }>("/password/reset", {
        token,
        email: trimmedEmail,
        password,
        password_confirmation: passwordConfirmation,
      });
      setMessage(response.message || "Password has been reset. You can now sign in.");
      setTimeout(() => navigate("/signin"), 2000);
    } catch (err: any) {
      const apiMessage =
        err?.data?.message ||
        err?.data?.errors?.email?.[0] ||
        err?.data?.errors?.password?.[0];

      const friendlyMessage =
        apiMessage || "Failed to reset password. Please try again.";
      setError(friendlyMessage);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="flex flex-col flex-1">
      <div className="w-full max-w-md pt-10 mx-auto">
        <Link
          to="/signin"
          className="inline-flex items-center text-sm text-gray-500 transition-colors hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
        >
          ← Back to sign in
        </Link>
      </div>
      <div className="flex flex-col justify-center flex-1 w-full max-w-md mx-auto">
        <div className="mb-5 sm:mb-8">
          <h1 className="mb-2 font-semibold text-gray-800 text-title-sm dark:text-white/90 sm:text-title-md">
            Reset Password
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Enter your new password below to regain access to your account.
          </p>
        </div>
        <form onSubmit={handleSubmit} className="space-y-6">
          {message && (
            <div className="p-3 text-sm text-[var(--okt)] bg-[var(--okbg)] border border-[var(--rule)] rounded-lg dark:bg-[var(--okbg)] dark:text-[var(--okt)] dark:border-[var(--rule)]">
              {message}
            </div>
          )}
          {error && (
            <div className="p-3 text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg dark:bg-red-900/20 dark:text-red-400 dark:border-red-800">
              {error}
            </div>
          )}
          <div>
            <Label>
              Email <span className="text-error-500">*</span>
            </Label>
            <Input
              type="email"
              value={email}
              placeholder="you@example.com"
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>
          <div>
            <Label>
              New Password <span className="text-error-500">*</span>
            </Label>
            <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                value={password}
                placeholder="Enter new password"
                onChange={(e) => setPassword(e.target.value)}
                className="h-11 w-full rounded-lg border appearance-none px-4 py-2.5 pr-11 text-sm shadow-theme-xs placeholder:text-gray-400 focus:outline-none focus:ring-2 bg-transparent text-gray-800 border-gray-300 focus:border-brand-500 focus:ring-brand-500/20 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90 dark:placeholder:text-white/30 dark:focus:border-brand-500"
                autoComplete="new-password"
              />
              <button
                type="button"
                onClick={() => setShowPassword((v) => !v)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                aria-label={showPassword ? "Hide password" : "Show password"}
                title={showPassword ? "Hide password" : "Show password"}
              >
                {showPassword ? (
                  <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M3 3l18 18M10.58 10.58A2 2 0 0113.42 13.42M9.88 5.08A10.94 10.94 0 0112 5c7 0 10 7 10 7a18.5 18.5 0 01-3.22 4.26M6.11 6.11A18.7 18.7 0 002 12s3 7 10 7a10.98 10.98 0 005.23-1.36"
                    />
                  </svg>
                ) : (
                  <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M1.5 12S5.5 5 12 5s10.5 7 10.5 7-4 7-10.5 7S1.5 12 1.5 12Z"
                    />
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M12 15a3 3 0 100-6 3 3 0 000 6Z"
                    />
                  </svg>
                )}
              </button>
            </div>
          </div>
          <div>
            <Label>
              Confirm Password <span className="text-error-500">*</span>
            </Label>
            <div className="relative">
              <input
                type={showPasswordConfirmation ? "text" : "password"}
                value={passwordConfirmation}
                placeholder="Confirm new password"
                onChange={(e) => setPasswordConfirmation(e.target.value)}
                className="h-11 w-full rounded-lg border appearance-none px-4 py-2.5 pr-11 text-sm shadow-theme-xs placeholder:text-gray-400 focus:outline-none focus:ring-2 bg-transparent text-gray-800 border-gray-300 focus:border-brand-500 focus:ring-brand-500/20 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90 dark:placeholder:text-white/30 dark:focus:border-brand-500"
                autoComplete="new-password"
              />
              <button
                type="button"
                onClick={() => setShowPasswordConfirmation((v) => !v)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                aria-label={showPasswordConfirmation ? "Hide password" : "Show password"}
                title={showPasswordConfirmation ? "Hide password" : "Show password"}
              >
                {showPasswordConfirmation ? (
                  <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M3 3l18 18M10.58 10.58A2 2 0 0113.42 13.42M9.88 5.08A10.94 10.94 0 0112 5c7 0 10 7 10 7a18.5 18.5 0 01-3.22 4.26M6.11 6.11A18.7 18.7 0 002 12s3 7 10 7a10.98 10.98 0 005.23-1.36"
                    />
                  </svg>
                ) : (
                  <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M1.5 12S5.5 5 12 5s10.5 7 10.5 7-4 7-10.5 7S1.5 12 1.5 12Z"
                    />
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M12 15a3 3 0 100-6 3 3 0 000 6Z"
                    />
                  </svg>
                )}
              </button>
            </div>
          </div>
          <div>
            <Button className="w-full" size="sm" type="submit" disabled={submitting}>
              {submitting ? "Resetting password..." : "Reset password"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}


